import { CurrencyPipe, formatCurrency as angularFormatCurrency } from '@angular/common';

import { Formatter } from '../formatter';

import { CurrencySymbol } from './currency-symbol.enum';

export const currencyFormatter: Formatter = {
    /**
     * Format a currency value for encoding (ie prior to sending to the backend).
     * Defaults to a maximum of two decimals (rounded).
     */
    format(value: string, numberOfDecimals = 2): string {
        return getValue(value, !!numberOfDecimals).toFixed(numberOfDecimals).replace(/\.00$/, '');
    },
    /**
     * Format a currency value for display purposes.
     */
    formatDisplay(value: string, numberOfDecimals = 2): string {
        return currencyTransformer(getValue(value, !!numberOfDecimals), numberOfDecimals);
    }
};

export function currencyTransformer(value: number, numberOfDecimals: number, currencyCode = '', symbol = '') {
    let result = '';
    if (value !== undefined && value !== null) {
        const roundedValue = parseFloat(value.toFixed(numberOfDecimals));
        const digitsInfo = `1.${numberOfDecimals}-${numberOfDecimals}`;
        const currency = new CurrencyPipe('nl-NL');
        result = currency.transform(roundedValue, currencyCode, symbol, digitsInfo);
    }
    return result;
}

export function formatCurrency(
    value: number,
    currencyCode = 'EUR',
    display: 'code' | 'symbol' | 'symbol-narrow' = 'symbol',
    digits: string = null, // uses defaults for loaded locale file
    locale: string = 'nl-NL'
): string {
    if (value === undefined || value === null) {
        return null;
    }

    const currency = display === 'code' ? currencyCode : CurrencySymbol[currencyCode];
    return angularFormatCurrency(value, locale, currency, currencyCode, digits);
}

/**
 * Parse string values to their numeric equivalent regardless of delimiter notation.
 * Valid string values may be:
 *     ,12
 *     0,12
 *     123,45
 *     1.234,56
 *     1.234.567,89
 *     .12
 *     0.12
 *     123.45
 *     1,234.56
 *     1,234,567.89
 */
function getValue(stringValue: string, hasDecimals: boolean = true): number {
    const dutchValue = formatDutch(stringValue);
    const chunks = dutchValue.split(/[,.]/g);
    const delimiters = dutchValue.split(/[^,]/g).filter((o) => o);
    const { length } = delimiters;
    let containsDecimals = length > 0;
    if (length > 1) {
        const delimiterFirst = delimiters[0];
        const delimiterLast = delimiters[length - 1];
        // assume all delimiters are equal when first and last delimiter are
        if (delimiterFirst === delimiterLast) {
            containsDecimals = false;
        }
    }
    if (containsDecimals && hasDecimals) {
        chunks.splice(chunks.length - 1, 0, '.');
    }
    return parseFloat(chunks.join(''));
}

export function formatDutch(value: string): string {
    // Replace if we match 1,123,124 and optional decimals
    if (value.match(/(,\d{3}){2}(\.\d+)?$/g)) {
        return value.replace(/,/g, '@').replace(/\./g, ',').replace(/@/g, '.');
    }

    // Replace last character if .00 or .0 to dutch ,00
    if (value.match(/\.\d{1,2}$/)) {
        return value.replace(',', '').replace(/\.\d{1,2}$/, (match) => match.replace('.', ','));
    }

    return value;
}
